
import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/components/auth/AuthProvider";
import { Link } from "react-router-dom";

interface ClaimDialogProps {
  centerName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isSubmitting: boolean;
  formData: {
    role: string;
    email: string;
    contactPhone: string;
    verificationNote: string;
  };
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onSelectChange: (value: string) => void;
  onSubmit: () => Promise<void>;
}

export function ClaimDialog({ 
  centerName, 
  open, 
  onOpenChange, 
  isSubmitting, 
  formData, 
  onInputChange, 
  onSelectChange,
  onSubmit
}: ClaimDialogProps) {
  const { user } = useAuth();
  
  // Handle the claim submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Claim {centerName}</DialogTitle>
          <DialogDescription>
            Provide your details to verify your association with this nursing home.
          </DialogDescription>
        </DialogHeader>
        
        {!user ? (
          <div className="space-y-4 py-4">
            <p className="text-center text-sm text-muted-foreground">
              You need to be signed in to claim a nursing home profile.
            </p>
            <div className="flex flex-col space-y-2">
              <Button asChild variant="default">
                <Link to={`/auth/login?redirectTo=${encodeURIComponent(window.location.pathname)}`}>
                  Sign In
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to={`/auth/register?redirectTo=${encodeURIComponent(window.location.pathname)}`}>
                  Create an Account
                </Link>
              </Button>
            </div>
            <div className="text-xs text-center text-muted-foreground mt-4">
              <p>Claiming this profile will give you access to:</p>
              <ul className="list-disc list-inside mt-2 text-left">
                <li>Manage your nursing home's profile</li>
                <li>Access leads from interested families</li>
                <li>Update your facility's information</li>
                <li>Respond to inquiries</li>
              </ul>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="role">Your Role at {centerName}</Label>
                <Select 
                  name="role" 
                  value={formData.role} 
                  onValueChange={onSelectChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select your role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="owner">Owner</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="administrator">Administrator</SelectItem>
                    <SelectItem value="director">Director of Care</SelectItem>
                    <SelectItem value="staff">Staff Member</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="email">Work Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={onInputChange}
                  placeholder="your.name@nursinghome.com"
                  required
                />
                <p className="text-xs text-muted-foreground">Preferably use your nursing home email address for faster verification.</p>
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="contactPhone">Contact Phone</Label>
                <Input
                  id="contactPhone"
                  name="contactPhone"
                  type="tel"
                  value={formData.contactPhone}
                  onChange={onInputChange}
                  placeholder="Your direct number"
                />
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="verificationNote">Additional Information</Label>
                <Textarea
                  id="verificationNote"
                  name="verificationNote"
                  value={formData.verificationNote}
                  onChange={onInputChange}
                  placeholder="Please provide any additional details that might help us verify your association with this nursing home."
                  rows={3}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Submitting..." : "Submit Claim Request"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
